import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  getTrainingEntryDeleteContext,
  groupDeleteTrainingEntry,
} from '../api/training';

function formatHours(value) {
  const totalMinutes = Math.round(Number(value || 0) * 60);
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')} h`;
}

function requestLabel(actionType) {
  if (actionType === 'create') return 'Eintragsanfrage';
  if (actionType === 'delete') return 'Löschanfrage';
  return 'Änderungsanfrage';
}

function statusLabel(status) {
  if (status === 'pending-delete') return 'Löschanfrage offen';
  if (status === 'pending-update') return 'Änderungsanfrage offen';
  return null;
}

function LogbookEntryDeleteDialog({ entryId, open, onClose, onDeleted }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [context, setContext] = useState(null);
  const [targets, setTargets] = useState([]);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOwnEntry, setDeleteOwnEntry] = useState(false);
  const [error, setError] = useState('');

  const loadContext = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getTrainingEntryDeleteContext(entryId);
      setContext(data);
      setTargets((data.targets || []).map((target) => ({ ...target, selected: false })));
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Löschinformationen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !entryId) return;
    setReason('');
    setDeleteOwnEntry(false);
    loadContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId, open]);

  const toggleTarget = (index, selected) => {
    setTargets((current) => current.map((target, targetIndex) => (
      targetIndex === index ? { ...target, selected } : target
    )));
  };

  const handleDelete = async () => {
    const selectedTargets = targets.filter((target) => target.selected);
    if (selectedTargets.length > 0 && !reason.trim()) {
      setError('Bitte eine Begründung für die Löschungen in weiteren Logbüchern angeben.');
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await groupDeleteTrainingEntry(entryId, {
        reason: reason.trim(),
        deleteOwnEntry,
        targets: selectedTargets.map((target) => ({
          targetKind: target.targetKind,
          entryId: target.entryId,
          requestId: target.requestId,
          requestRevision: target.requestRevision,
        })),
      });
      onDeleted();
    } catch (deleteError) {
      setError(deleteError.response?.data?.error || 'Eintrag konnte nicht gelöscht werden.');
      if (deleteError.response?.status === 409) await loadContext();
    } finally {
      setDeleting(false);
    }
  };

  const selectedCount = targets.filter((target) => target.selected).length;
  const pendingRequests = context?.pendingRequests || [];

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>Logbucheintrag löschen</DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 180 }}><CircularProgress /></Box>}
        {!loading && context && (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {(deleteOwnEntry || selectedCount > 0) && (
              <Alert severity={deleteOwnEntry ? 'warning' : 'info'}>
                {deleteOwnEntry && selectedCount > 0
                  ? 'Der Eintrag in deinem Logbuch wird endgültig gelöscht. Für ausgewählte andere Logbücher werden Löschanfragen erstellt.'
                  : deleteOwnEntry
                    ? 'Der Eintrag in deinem Logbuch wird endgültig gelöscht.'
                    : 'Dein eigener Eintrag bleibt erhalten. Für ausgewählte andere Logbücher werden nur Löschanfragen erstellt.'}
              </Alert>
            )}

            <FormControlLabel
              control={<Checkbox checked={deleteOwnEntry} onChange={(event) => setDeleteOwnEntry(event.target.checked)} />}
              label="Eigenen Logbucheintrag löschen"
            />

            {context.canManageGroup && targets.length > 0 && (
              <Stack spacing={1.25}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Löschanfrage für weitere Logbücher</Typography>
                {targets.map((target, index) => (
                  <Box key={`${target.targetKind}-${target.entryId || target.requestId}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                      <FormControlLabel
                        control={<Checkbox checked={target.selected} disabled={!target.canSelect} onChange={(event) => toggleTarget(index, event.target.checked)} />}
                        label={target.fullName}
                      />
                      {statusLabel(target.status) && <Chip size="small" variant="outlined" label={statusLabel(target.status)} />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {`${target.summary?.date || '-'} · ${target.summary?.entryType || '-'} · ${formatHours(target.summary?.hours)}`}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}

            {context.canManageGroup && pendingRequests.filter((request) => !targets.some((target) => target.pendingRequestId === request.id)).length > 0 && (
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Weitere offene Anfragen dieser Gruppe</Typography>
                {pendingRequests
                  .filter((request) => !targets.some((target) => target.pendingRequestId === request.id))
                  .map((request) => (
                    <Stack key={request.id}>
                      <Typography variant="body2">{`${requestLabel(request.actionType)} für ${request.targetUserName}`}</Typography>
                    </Stack>
                  ))}
              </Stack>
            )}

            {selectedCount > 0 && (
              <TextField required fullWidth multiline minRows={2} label="Begründung für Löschanfragen" value={reason} onChange={(event) => setReason(event.target.value)} />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={deleting}>Abbrechen</Button>
        <Button color="error" variant="contained" startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteOutlineIcon />} onClick={handleDelete} disabled={loading || deleting || !context || (!deleteOwnEntry && selectedCount === 0) || (selectedCount > 0 && !reason.trim())}>
          {!deleteOwnEntry && selectedCount === 0
            ? 'Auswahl erforderlich'
            : deleteOwnEntry
            ? (selectedCount > 0 ? `Eigenen löschen und ${selectedCount} anfragen` : 'Eigenen Eintrag löschen')
            : `${selectedCount} Löschanfrage${selectedCount === 1 ? '' : 'n'} stellen`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LogbookEntryDeleteDialog;
